import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";

// ** MUI Components
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Popover,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from "@mui/material";

import { CircularProgress, } from "@mui/material";

// ** Custom Components
import Icon from "components/icon";
import CustomTextField from "components/mui/text-field";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CustomAutocomplete from "components/mui/autocomplete";
import { useIndexLogic } from "screens/Services/logic/index-logic";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from 'react-toastify';
import CreateServiceDialog from "screens/Services/component/CreateService";


const AddService = React.memo(({ 
  open, 
  handleServiceClose, 
  serviceList,
  loadingRecords,
  localSearch,
  handleLocalSearch,
  handleClearSearch,
  fetchServices,
  selectedServices,
  setSelectedServices,
  addService,
}) => {

  
  const { IndexProps } = useIndexLogic({ toast});

  const {
    openServiceDlg,
    handleCloseServiceDlg,
    handleOpenServiceDlg,
    serviceContent,
    ServiceProps
  } = IndexProps;
  
  const [anchorEl, setAnchorEl] = useState(null);
  const popoverContentRef = useRef(null);
  const searchInputRef = useRef(null);
  const isPopoverOpen = Boolean(anchorEl);

 
  useEffect(() => {
    if (isPopoverOpen && searchInputRef.current) {
    
      searchInputRef.current.focus();
      const input = searchInputRef.current;
      const length = input.value.length;
      input.setSelectionRange(length, length);
    }
  }, [isPopoverOpen]);

   
  // Scroll handler implementation
  const handlePopoverScroll = useCallback(() => {
    if (loadingRecords || !popoverContentRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = popoverContentRef.current;
    if (scrollHeight - (scrollTop + clientHeight) < 50) {
      fetchServices(false);
    }
  }, [loadingRecords, localSearch, handleLocalSearch]);

  const getItemId = useCallback((item) => {
    if (!item.serviceOfferingID) {
      return `gen-${item.industryName}-${item.serviceOfferingName}`
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
    }
    return item.serviceOfferingID;
  }, []);


  const handleToggle = useCallback((item) => {
    setSelectedServices(prev => {
      const exists = prev.some(i => i.serviceOfferingID === item.serviceOfferingID);
      if (exists) {
        return prev.filter(i => i.serviceOfferingID !== item.serviceOfferingID);
      } else {
        return [...prev, { 
          serviceOfferingID: item.serviceOfferingID,
          label: `${item.industryName} - ${item.serviceOfferingName}`,
          industryName: item.industryName,
          serviceOfferingName: item.serviceOfferingName
        }];
      }
    });
  }, [setSelectedServices]);

 const memoizedServiceItems = useMemo(() => {
  const activeServices = serviceList
    .filter(x => x.isActive)
    .sort((a, b) => {
      const labelA = `${a.industryName} - ${a.serviceOfferingName}`.toLowerCase();
      const labelB = `${b.industryName} - ${b.serviceOfferingName}`.toLowerCase();
      return labelA.localeCompare(labelB);
    });

  return activeServices.map(service => (
    <TreeItem
      key={getItemId(service)}
      itemId={getItemId(service)}
      label={
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedServices.some(
                item => item.serviceOfferingID === service.serviceOfferingID
              )}
              onChange={() => handleToggle(service)}
            />
          }
          label={`${service.industryName} - ${service.serviceOfferingName}`}
          sx={{ ml: 0 }}
        />
      }
    />
  ));
}, [serviceList, selectedServices, handleToggle, getItemId]);


  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);  

 
  return (
    <>
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleServiceClose}  PaperProps={{
          sx: {
            height: "100%",
            maxHeight: "50vh",
          },
        }}>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #D9D9D9",
          paddingBottom: "20px !important",
        }}
      >
        <Typography variant="h4">Add Service</Typography>
        <IconButton onClick={handleServiceClose}>
          <Icon icon="tabler:x" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box>        
          <CustomTextField
            label="Service"
            value={selectedServices.length > 0 
              ? `${selectedServices.length} service(s) selected` 
              : ""}
            placeholder="Select"
            onClick={handleClick}
            readOnly
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <ArrowDropDownIcon />
                </InputAdornment>
              ),
            }}
          />
          <Popover
            open={isPopoverOpen}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ horizontal: 'left' }}
            TransitionProps={{
              onEntered: () => {
                if (searchInputRef.current) {
                  const input = searchInputRef.current;
                  input.focus();
                  const length = input.value.length;
                  input.setSelectionRange(length, length);
                }
              }
            }}
            sx={{ 
              '& .MuiPopover-paper': { 
                width: '450px', 
                maxHeight: '340px',
                overflow: 'hidden',
                cursor: 'text', // Force text cursor on entire popover
              } 
            }}
          >
            <Box sx={{
                  maxHeight: { xs: 180, md: 250, lg: 350, xl: 450 },
                  minWidth: 300,
                  p: 2,
                }}>
              <Box
                onClick={(e) => {
                  // Prevent click from bubbling up and ensure focus stays on input
                  e.stopPropagation();
                  if (searchInputRef.current) {
                    searchInputRef.current.focus();
                  }
                }}
              >
                <CustomTextField
                  fullWidth
                  placeholder="Search services..."
                  value={localSearch}
                  onChange={handleLocalSearch}
                  inputRef={searchInputRef}
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon icon="tabler:search" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {localSearch && (
                          <IconButton
                            edge="end"
                            onClick={handleClearSearch}
                            aria-label="clear"
                            sx={{ mr: 2 }}
                          >
                            <Icon icon="tabler:x" />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputBase-root': {
                      cursor: 'text !important',
                    },
                    '& .MuiInputBase-input': {
                      cursor: 'text !important',
                      caretColor: 'auto !important', // Ensure caret is visible
                    }
                  }}
                  onFocus={(e) => {
                    // Ensure cursor is at the end when focused
                    const length = e.target.value.length;
                    e.target.setSelectionRange(length, length);
                  }}
                />
              </Box>
              
              <Box 
                ref={popoverContentRef}
                onScroll={handlePopoverScroll}
                sx={{ 
                  maxHeight: '200px', 
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1
                }}
              >
                <SimpleTreeView getItemId={(item) => item.serviceOfferingID}>
                  <TreeItem
                    label={
                      <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        p: 1,
                        fontWeight: 'bold', 
                      }}
                      onClick={handleOpenServiceDlg}
                    >
                      {/* <Icon icon="tabler:plus" /> */}
                      <Typography sx={{ fontWeight: 'inherit' }}>+ Create New Service</Typography> 
                    </Box>
                    }
                  />
                  {memoizedServiceItems}
                  {loadingRecords && (
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' , mb:5}}>
                      <CircularProgress size={20} />
                    </Box>
                  )}
                </SimpleTreeView>
              </Box>
            </Box>
          </Popover>
        </Box>
      </DialogContent>
      <DialogActions
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        padding: "20px 24px !important",
        boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
        zIndex: 1,
      }}
      >
        <Button variant="contained" color ='info' onClick={handleServiceClose}>
          Cancel
        </Button>
        <Button variant="contained" color="main" onClick={addService} disabled={selectedServices.length === 0}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
     <CreateServiceDialog
            open={openServiceDlg}
            handleClose={handleCloseServiceDlg}
            serviceContent={serviceContent}
            ServiceProps={ServiceProps}
          />
          </>
  );
});

export default AddService;